import type { HTMLInputTypeAttribute, ReactNode } from 'react';

import { Input as BaseInput, Button, type InputProps, Toggle } from '@base-ui/react';

import { clsx, clsxs } from '~/styles/utils';
import { filterBoolean } from '~/utils/arrays';
import { mergeRefs } from '~/utils/mergeRefs';

import { Icon, type IconName } from '../../Icon/Icon';
import { adornmentStyle, controlStateTarget, controlThemeStyle, prefixIconStyle } from '../form.css';
import { useAccessibleAffixes } from '../form.hooks';
import {
  clearButtonHiddenStyle,
  clearButtonStyle,
  inputLengthStyle,
  inputStyle,
  inputWrapperStyle,
  revealPasswordToggleStyle,
} from './Input.css';
import {
  type ClearableInputOnValueChange,
  useClearInputHandler,
  useControlledValue,
  useInputFocusHandler,
  useMaxInputLengthHandler,
  usePasswordInputHandler,
} from './Input.hooks';

type Props = InputProps & {
  adornment?: ReactNode;
  clearable?: boolean;
  icon?: IconName;
  maxLength?: number;
  onValueChange?: ClearableInputOnValueChange;
  prefix?: string;
  suffix?: string;
  transformValue?: (value: string) => string;
  type?: Extract<HTMLInputTypeAttribute, 'email' | 'password' | 'search' | 'tel' | 'text' | 'url'>;
};

export const Input = ({ adornment, clearable = false, icon, maxLength, prefix, suffix, transformValue, ...delegated }: Props) => {
  const { onValueChange, value } = useControlledValue(delegated);
  const { inputRef, inputWrapperProps } = useInputFocusHandler();
  const { clearButtonProps } = useClearInputHandler({ onValueChange });
  const { inputType, isPasswordRevealed, revealPasswordButtonProps } = usePasswordInputHandler(delegated);
  const { currentLength, lengthSuffixRef, limitValueLength } = useMaxInputLengthHandler({ maxLength, value });
  const { ariaDescribedBy, prefixProps, suffixProps } = useAccessibleAffixes({ prefix, suffix });

  return (
    <BaseInput
      {...delegated}
      className={clsx(controlStateTarget, inputStyle)}
      onValueChange={(value, eventDetails) => {
        const transformations = [transformValue, limitValueLength].filter(filterBoolean);
        const transformedValue = transformations.reduce((value, fn) => fn(value), value);

        onValueChange(transformedValue, eventDetails);
      }}
      ref={mergeRefs(inputRef, delegated.ref)}
      render={(inputProps, state) => (
        <div className={clsxs(controlThemeStyle, delegated.className)(state)}>
          <div className={inputWrapperStyle} {...inputWrapperProps}>
            {icon && <Icon className={prefixIconStyle} name={icon} />}

            {adornment && <span className={adornmentStyle}>{adornment}</span>}

            {prefix && <span {...prefixProps}>{prefix}</span>}

            <input
              {...inputProps}
              aria-describedby={[inputProps['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined}
            />

            {maxLength !== undefined && !state.disabled && (
              <span className={inputLengthStyle} ref={lengthSuffixRef}>
                {currentLength}/{maxLength}
              </span>
            )}

            {clearable && !state.disabled && (
              <Button className={clsxs(clearButtonStyle, !state.filled && clearButtonHiddenStyle)} {...clearButtonProps}>
                <Icon name="CloseSmall" size={24} />
              </Button>
            )}

            {delegated.type === 'password' && !state.disabled && (
              <Toggle className={revealPasswordToggleStyle} {...revealPasswordButtonProps}>
                <Icon name={isPasswordRevealed ? 'VisibilityNo' : 'Visibility'} size={24} />
              </Toggle>
            )}

            {suffix && <span {...suffixProps}>{suffix}</span>}
          </div>
        </div>
      )}
      type={inputType}
      value={value}
    />
  );
};
